import { OffscreenCanvas } from "@/components/OffscreenCanvas";
import { WorkerStats } from "@/components/WorkerStats";
import {
  DiceActionTypes,
  DiceRollActionTypes,
  getDiceServiceUrl,
  getMediaUrl,
  isDebug,
  WorkerPayloadTypes,
} from "@/constants";
import { customSetManifestFileName } from "@/constants/diceConfig";
import { Context } from "@/context";
import { getManifestData } from "@/helpers/getManifestData";
import { playSound } from "@/helpers/playSound";
import { preloadAssets } from "@/helpers/preloadAssets";
import { useDicePreviewerStore } from "@/hooks/useDicePreviewerStore";
import { useUserDiceDataStore } from "@/hooks/useUserDiceDataStore";
import type { DiceSet, Manifest } from "@/types";
import { key } from "@dndbeyond/message-broker-lib/dist/lib/constants";
import { useContext, useEffect, useRef, type FC } from "react";

interface DiceCanvasProps {
  worker?: Worker;
  gameId?: string;
  stt?: string;
  setId?: string;
  diceSets: DiceSet[];
  toastCallback?: (message: string) => void;
}

export const DiceCanvas: FC<DiceCanvasProps> = ({
  worker,
  gameId,
  stt,
  setId,
  diceSets,
  toastCallback,
}) => {
  const { nodeEnv, env, user } = useContext(Context);
  const customUrlMap = useDicePreviewerStore((state) => state.customUrlMap);
  const mediaUrl = getMediaUrl(String(nodeEnv), String(env));
  const diceServiceUrl = getDiceServiceUrl(String(nodeEnv), String(env));
  const settings = useUserDiceDataStore(
    (state) => state.getUserDiceData(Number(user?.id))?.settings,
  );
  const volume = settings?.volume !== undefined ? settings?.volume : 1;
  const shadowQuality = settings?.shadowQuality || 3;
  const activeSources = useRef<Set<string>>(new Set());

  useEffect(() => {
    const handleMessage = async ({ data: { type, payload } }: MessageEvent) => {
      if (isDebug && type !== WorkerPayloadTypes.WorkerStats) {
        console.log("[DiceCanvas] Message from worker:", type, payload);
      }

      if (type === DiceRollActionTypes.Fulfilled) {
        window[key]?.dispatch(payload);
      } else if (type === WorkerPayloadTypes.PlaySound) {
        if (!payload.isCollision && activeSources.current.has(payload.url))
          return;

        activeSources.current.add(payload.url);
        playSound(
          payload.url,
          payload.volume * (volume ?? 1),
          activeSources.current,
        );
      } else if (type === WorkerPayloadTypes.ComponentMounted) {
        const data: Record<string, Manifest> = {};
        const setIds: string[] = [];

        if (gameId && stt) {
          try {
            const { data } = await (
              await fetch(
                `${diceServiceUrl}/diceuserconfig/v1/getsetidsincampaign/${gameId}`,
                {
                  headers: {
                    Authorization: `Bearer ${stt}`,
                  },
                },
              )
            ).json();

            setIds.push(...data);
          } catch (error) {
            if (isDebug) {
              console.error(
                `[DiceCanvas] Error fetching set IDs for game ID "${gameId}":`,
                error,
              );
            }
          }
        } else {
          if (setId) {
            setIds.push(setId);
          }
        }

        await Promise.all(
          setIds.map(async (setId) => {
            const manifestUrl =
              customUrlMap?.[customSetManifestFileName] ||
              diceSets.find(({ id }) => id === setId)?.manifestUrl;

            if (!manifestUrl) {
              if (isDebug) {
                console.warn(
                  `[DiceCanvas] Manifest URL for set ID "${setId}" not found.`,
                );
              }

              return;
            }

            data[setId] = await getManifestData({
              mediaUrl,
              setId,
              manifestUrl,
              customUrlMap,
            });
          }),
        );

        worker?.postMessage({
          type: WorkerPayloadTypes.Manifests,
          payload: data,
        });

        if (isDebug) {
          console.log("[DiceCanvas] Loaded manifests:", data);
        }

        worker?.postMessage({
          type: WorkerPayloadTypes.ShadowQuality,
          payload: shadowQuality,
        });

        if (isDebug) {
          console.log(
            "[DiceCanvas] Sent shadow quality to worker:",
            shadowQuality,
          );
        }

        worker?.postMessage({
          type: WorkerPayloadTypes.DiceSets,
          payload: diceSets,
        });

        if (isDebug) {
          console.log("[DiceCanvas] Sent dice sets to worker:", diceSets);
        }

        preloadAssets(mediaUrl, Object.values(data), customUrlMap);

        if (isDebug) {
          console.log("[DiceCanvas] Preloading assets complete.");
        }
      } else if (type === DiceActionTypes.SetUpdate) {
        preloadAssets(mediaUrl, [payload], customUrlMap);
      } else if (type === WorkerPayloadTypes.ToastMessage) {
        toastCallback?.(payload);
      }
    };

    worker?.addEventListener("message", handleMessage);

    return () => {
      worker?.removeEventListener("message", handleMessage);
    };
  }, [
    customUrlMap,
    diceServiceUrl,
    diceSets,
    gameId,
    mediaUrl,
    setId,
    shadowQuality,
    stt,
    toastCallback,
    volume,
    worker,
  ]);

  return (
    worker && (
      <>
        <OffscreenCanvas
          worker={worker}
          frameloop="demand"
          shadows
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999999999,
            pointerEvents: "none",
          }}
        />
        {isDebug && <WorkerStats worker={worker} />}
      </>
    )
  );
};
